import { makeObservable, observable, computed, action } from 'mobx';
import { Dictionary, sortBy as _sortBy } from 'lodash';
import type { MarkerData, PolylineData } from '@bringg/react-components/dist/types/maps.consts';
import type { DeliveryBlock, Location } from '@bringg/types';
import type { Directions } from '@bringg/types/types/directions';

import type { IconsProps, JSXIconWithToolTip, StartEndMarker } from '../../components/routes-planner/routes-planner';
import type RoutesPlannerStore from '../../stores/routes-planner-store';
import MarkerBlack from '../../icons/marker-black.png';
import { createDirectionsByRunIdMap, getMarkers, getPath } from '../../components/routes-planner/utils';
import { markersProvider } from '../../services/map/markers/markers-provider';

interface MapStoreProps {
	home?: Location;
	directions?: Directions[];
	mapIcons?: { driver: JSXIconWithToolTip; parking: JSXIconWithToolTip; home: JSXIconWithToolTip };
	deliveryBlocks?: DeliveryBlock[];
}

export default class MapStore {
	rootStore: RoutesPlannerStore;
	icons: IconsProps;
	home?: Location;
	mapCenter: Location = null;
	runToDirections: Dictionary<Directions> = {};
	routesStartEndMarkers: Map<number, { startMarker: StartEndMarker; endMarker: StartEndMarker }> = new Map();
	mapProps: MapStoreProps;

	constructor(rootStore: RoutesPlannerStore, mapProps: MapStoreProps) {
		makeObservable(this, {
			mapCenter: observable,
			mapMarkers: computed,
			mapPolylines: computed,
			runToDirections: observable,
			setMapCenter: action,
			setDirections: action
		});
		this.mapProps = mapProps;
		this.rootStore = rootStore;
		this.icons = {
			home: { icon: mapProps.mapIcons?.home, iconOptions: { sdf: true } },
			marker: { icon: MarkerBlack, iconOptions: { sdf: true } },
			driver: { icon: mapProps.mapIcons?.driver, iconOptions: { sdf: true } },
			parking: { icon: mapProps.mapIcons?.parking, iconOptions: { sdf: true } }
		};
		this.routesStartEndMarkers = markersProvider.arrangeRoutesStartEndMarkers({
			directions: mapProps.directions,
			routesById: this.rootStore.routesById,
			routeInfoByIndex: this.rootStore.routeInfoByIndex,
			drivers: this.rootStore.driversById,
			parkingSpots: this.rootStore.parkingSpots,
			translations: this.rootStore.translations,
			vehicles: this.rootStore.vehicles,
			deliveryBlocksByDriver: this.rootStore.deliveryBlocksByDriver,
			deliveryBlocksByVehicle: this.rootStore.deliveryBlocksByVehicle
		});
		this.home = mapProps.home;
		this.mapCenter = this.home;
		this.runToDirections = createDirectionsByRunIdMap(mapProps.directions);
	}

	get mapMarkers() {
		const homeMarker: MarkerData[] = this.home
			? [
					{
						id: 'homeMarkerId',
						location: this.home,
						icon: this.icons.home.icon(),
						iconOptions: this.icons.home.iconOptions
					}
			  ]
			: [];

		const { runsToDisplay, runToTasksInPresentedDate, routesColors } = this.rootStore;

		const markers = runsToDisplay.reduce((markersArr, runId) => {
			const groupTasks = runToTasksInPresentedDate[runId];
			const sortedTasks = _sortBy(groupTasks, task => task.priority);
			const groupColor = routesColors.get(runId);
			const currentRoute = this.routesStartEndMarkers.get(runId);

			const markers: MarkerData[] = getMarkers(
				sortedTasks,
				runId,
				groupColor,
				this.icons.marker,
				this.rootStore.inProgressEtaCalculationsList
			);
			currentRoute?.startMarker &&
				markers.push(
					markersProvider.createStartOrEndMarker(
						'start',
						currentRoute?.startMarker,
						groupColor,
						this.icons,
						runId
					)
				);

			currentRoute?.endMarker &&
				markers.push(
					markersProvider.createStartOrEndMarker(
						'end',
						currentRoute?.endMarker,
						groupColor,
						this.icons,
						runId
					)
				);

			return [...markersArr, ...markers];
		}, []);

		return [...homeMarker, ...markers];
	}

	get mapPolylines() {
		const { runsToDisplay, routesColors } = this.rootStore;

		return runsToDisplay.map(runId => {
			const groupDirections = this.runToDirections[runId];
			const groupColor = routesColors.get(runId);

			const polyline: PolylineData = {
				id: runId,
				path: getPath(groupDirections),
				color: groupColor,
				strokeOpacity: this.rootStore.isEtaHasActiveCalculation(runId) ? 0.25 : 0.5,
				strokeWeight: 6
			};

			return polyline;
		});
	}

	setMapCenter = (location: Location) => {
		this.mapCenter = location;
	};

	setDirectionsOnMapProps = (runToDirectionsUpdate: Dictionary<Directions>) => {
		if (this.mapProps.directions.length) {
			const runsToUpdateDirections = Object.keys(runToDirectionsUpdate);
			const directionsByRunID = {};
			this.mapProps.directions.forEach(direction => (directionsByRunID[direction.run_id] = direction));
			runsToUpdateDirections.forEach(runId => (directionsByRunID[runId] = runToDirectionsUpdate[runId]));
			this.mapProps.directions = Object.values(directionsByRunID);
		} else {
			this.mapProps.directions = Object.values(runToDirectionsUpdate);
		}

		this.routesStartEndMarkers = markersProvider.arrangeRoutesStartEndMarkers({
			directions: this.mapProps.directions,
			routesById: this.rootStore.routesById,
			routeInfoByIndex: this.rootStore.routeInfoByIndex,
			drivers: this.rootStore.driversById,
			parkingSpots: this.rootStore.parkingSpots,
			translations: this.rootStore.translations,
			vehicles: this.rootStore.vehicles,
			deliveryBlocksByDriver: this.rootStore.deliveryBlocksByDriver,
			deliveryBlocksByVehicle: this.rootStore.deliveryBlocksByVehicle
		});
	};

	setDirections = (runToDirectionsUpdate: Dictionary<Directions>) => {
		Object.assign(this.runToDirections, runToDirectionsUpdate);
		this.setDirectionsOnMapProps(runToDirectionsUpdate);
	};

	getRunIdsWithNoDirections(runIds: number[]) {
		const runIdsWithDirections = Object.keys(this.runToDirections);
		return runIds.filter(runId => {
			return !runIdsWithDirections.find(item => item === runId.toString());
		});
	}
}
